import {
  ObjectStatuses,
  EntityActions,
  EntityCodes,
} from '../global-entities/global-enums';
import { DateTime } from 'luxon';
import { isNumber, assignIn, orderBy, keyBy } from 'lodash';
import { HealthStates, MetricTypes, MetricWarningOrders } from './enum-metric';
import { ListRequest } from '../request-models/request-models';
import { CacheManagementService } from '@core/services/cache-management.service';
import { SimpleResponse } from '../response-models/response-models';

export class AssetDatedStat {
  public AssetId: number;
  public Dated: Date;
  public RunningWordOrders: number;
  public PendingWordOrders: number;
  public PMToStartCounts: number;
  public LastMaintenanceDate: Date;
}

export class MetricSearch {
  public PropertyId: number;
  public MetricId: number;
}

export class AssetMetricSearch {
  public AssetMetricId?: number;
  public PropertyId: number;
  public AssetIds: Array<number>;
  public MetricId?: number;
  public MetricTypeId?: number;
  public setDefault() {
    this.AssetIds = [];
  }
}

export class MetricRecordAsset {
  public AssetMetricId: number;
  public Reading: number;
  public EntityId?: number;
  public EntityCode: string;
  public Notes: string;
  public ReadingDate: DateTime;
}

export class MetricRecordAssetMultiple {
  public Readings: MetricRecordAsset[];
}

export class AssetMetric {
  public AssetMetricId: number;
  public AssetId: number;
  public MetricId: number;
  public PropertyId: number;
  public Status: ObjectStatuses;
  public MetricTypeId: MetricTypes;
  public AttachedOn: DateTime;
  public DettachedOn?: DateTime;
  public WarningOrder?: MetricWarningOrders;
  public GoodValue?: number;
  public AttentionValue?: number;
  public Reading?: number;
  public Notes: string;
  public LastResetOn?: DateTime;
  public ResetBy?: number;
  public HealthState?: HealthStates;
  public StartDate?: DateTime;
  public EndDate?: DateTime;
  public HealthScore?: number;
  public MaxPossibleScore?: number;
  public CummalativeScore?: number;
  public RunningMaxScore?: number;
  public RunningCummalativeScore?: number;
  public ModifiedOn?: DateTime;
  public ModifiedBy?: number;
  public CreatedOn: DateTime;
  public CreatedBy: number;
}

export class AssetMetricUpdate {
  public AssetMetricId: number;
  public AssetId: number;
  public MetricId: number;
  public Action: EntityActions;
  public WarningOrder: number;
  public GoodValue: number;
  public AttentionValue: number;

  static createRequestFromAssetMetricViewModel(model: AssetMetricViewModel) {
    const requestModel = new AssetMetricUpdate();
    requestModel.AssetId = model.AssetId;
    requestModel.AssetMetricId = model.AssetMetricId;
    requestModel.AttentionValue = model.AttentionValue;
    requestModel.GoodValue = model.GoodValue;
    requestModel.WarningOrder = model.WarningOrder;
    requestModel.MetricId = model.MetricId;
    return requestModel;
  }
}

export class AssetMetricHistoryRequest extends ListRequest {
  public AssetMetricIds: number[] = [];
  public StartDate?: Date;
  public EndDate?: Date;
}

export class AssetMetricHistory {
  public AssetMetricHistoryId: number;
  public AssetMetricId: number;
  public MetricId: number;
  public MetricTypeId: MetricTypes;
  public Reading: number;
  public ReadingDate: DateTime;
  public Notes: string;
  public EntityId?: number;
  public EntityCode: string;
  public CreatedOn?: Date;
  public CreatedBy: number;
}

export class AssetMetricHistoryVW {
  public CreatedByStaffName: string;
  public CreatedOn: DateTime;
  public Source: string;
  public Reading: number;
  public MetricTypeId: MetricTypes;
  public ReadingDate: DateTime;

  static FromAssetMetricHistorySimpleResponse(
    propertyId: number,
    result: AssetMetricHistory[],
    cacheManagementService: CacheManagementService
  ): AssetMetricHistoryVW[] {
    const VWResult = result.map(
      (a) => new AssetMetricHistoryVW(propertyId, a, cacheManagementService)
    );
    return VWResult;
  }

  constructor(
    propertyId: number,
    historyItem: AssetMetricHistory,
    cacheManagementService: CacheManagementService
  ) {
    this.CreatedOn = DateTime.fromJSDate(historyItem.CreatedOn);
    this.Source = historyItem.EntityCode;
    this.Reading = historyItem.Reading;
    this.MetricTypeId = historyItem.MetricTypeId;
    this.ReadingDate = historyItem.ReadingDate;
    this.CreatedByStaffName = cacheManagementService.MasterData_GetMutli(
      propertyId,
      historyItem.CreatedBy,
      EntityCodes.Staff,
      null
    );
  }
}

export class AssetMetricStateHistoryRequest extends ListRequest {
  public AssetMetricIds: number[] = [];
  public StartDate?: Date;
  public EndDate?: Date;
}

export class AssetMetricStateHistory {
  public AssetMetricStateHistoryId: number;
  public AssetMetricId: number;
  public MetricId: number;
  public MetricTypeId: MetricTypes;
  public PropertyId: number;
  public AssetId: number;
  public Reading: number;
  public HealthState: HealthStates;
  public StartDate: Date;
  public EndDate: Date;
  public HealthScore?: number;
  public CummlativeScore?: number;
  public MaxPossibleScore?: number;
  public RunningMaxPossibleScore?: number;
  public RunningCummalativeScore?: number;
  public ModifiedOn?: Date;
  public ModifiedBy?: number;
  public CreatedOn: Date;
  public CreatedBy: number;
}

export class MetricUpdate {
  public MetricId?: number;
  public PropertyId: number;
  public Name: string;
  public MetricTypeId: MetricTypes;
  public Action: EntityActions;
  public MeasurementUnitId?: number;
}

export class Metric {
  public MetricId: number;
  public PropertyId: number;
  public Name: string;
  public MetricTypeId: MetricTypes;
  public MeasurementUnitId?: number;
  public Status: ObjectStatuses;
  public ModifiedOn: Date;
  public ModifiedBy: number;
  public CreatedOn: Date;
  public CreatedBy: number;
}

export class MetricViewModel extends Metric {
  public MetricTypeName: string;
  public MeasurementUnitName: string;
}

export class AssetMetricViewModel extends AssetMetric {
  public MetricName: string;
  public MeasurementUnitName: string;
  public HealthStateName?: string;
  public HealthStateClassName?: string;
  public ResetByName?: string;
  public ModifiedByName?: string;
  public CreatedByName?: string;
}

export type AssetMetricsVMResponse = SimpleResponse<AssetMetricViewModel[]>;

export class AssetMetricStateHistoryVM extends AssetMetricStateHistory {
  public MetricName: number;
  public ModifiedByStaffName: string;
  public HealthStateName?: string;

  static FromAssetMetricStateHistorySimpleResponse(
    result: AssetMetricStateHistory[],
    cacheManagementService: CacheManagementService
  ): AssetMetricStateHistoryVM[] {
    const allMetrics = {};
    const metricsById = (propertyId, metricId) => {
      let metrics = allMetrics[propertyId];
      if (!metrics) {
        allMetrics[propertyId] = keyBy(
          cacheManagementService.getCache(propertyId, EntityCodes.Metric) || [],
          'MetricId'
        );
        metrics = allMetrics[propertyId];
      }
      return metrics[metricId];
    };
    const VWResult = result.map((stateHistory) => {
      const vm = new AssetMetricStateHistoryVM(
        stateHistory,
        cacheManagementService
      );
      const metric = metricsById(vm.PropertyId, vm.MetricId);
      vm.MetricName = metric && metric.Name;
      return vm;
    });
    return orderBy(VWResult, ['ModifiedOn'], ['desc']);
  }

  constructor(
    historyItem: AssetMetricStateHistory,
    cacheManagementService: CacheManagementService
  ) {
    super();
    assignIn(this, historyItem);
    this.MetricName = historyItem.AssetMetricId;
    this.ModifiedByStaffName = cacheManagementService.MasterData_GetMutli(
      historyItem.PropertyId,
      historyItem.CreatedBy,
      EntityCodes.Staff,
      null
    );
  }
}

// Asset Metric Type Score
export class AssetMetricTypeScore {
  public AssetMetricTypeScoreId: number;
  public AssetId: number;
  public MetricTypeId: MetricTypes;
  public PropertyId: number;
  public HealthScore?: number;
  public MaxPossibleScore?: number;
  public CummalativeScore?: number;
  public RunningMaxPossibleScore?: number;
  public RunningCummalativeScore?: number;
  public ModifiedOn?: Date;
  public ModifiedBy?: number;
  public CreatedOn: Date;
  public CreatedBy: number;
}

export class AssetMetricTypeScoreVM extends AssetMetricTypeScore {
  public CalculatedHealthScore?: number;
  public HealthIndexClass?: string;
  static CalculateHealthScore(assetMetricTypeScore: AssetMetricTypeScore) {
    if (!assetMetricTypeScore) {
      return;
    }

    if (
      assetMetricTypeScore.CummalativeScore === null ||
      assetMetricTypeScore.MaxPossibleScore === null
    ) {
      return;
    }

    const runningMaxPossibleScore = isNumber(
      assetMetricTypeScore.RunningMaxPossibleScore
    )
      ? assetMetricTypeScore.RunningMaxPossibleScore
      : 0;
    const maxPossibleScore = isNumber(assetMetricTypeScore.MaxPossibleScore)
      ? assetMetricTypeScore.MaxPossibleScore
      : 0;
    const runningCummalativeScore = isNumber(
      assetMetricTypeScore.RunningCummalativeScore
    )
      ? assetMetricTypeScore.RunningCummalativeScore
      : 0;
    const cummalativeScore = isNumber(assetMetricTypeScore.CummalativeScore)
      ? assetMetricTypeScore.CummalativeScore
      : 0;

    if (maxPossibleScore + runningMaxPossibleScore === 0) {
      return;
    }

    return (
      (cummalativeScore + runningCummalativeScore) /
      (maxPossibleScore + runningMaxPossibleScore)
    );
  }
}

export type AssetMetricTypeScoreResponse = SimpleResponse<
  AssetMetricTypeScoreVM[]
>;

// Asset Metric Type Score Search
export class AssetMetricTypeScoreSearch {
  public AssetIds: number[];
}
